/**
 * SEO 工具类 - 动态设置页面 meta 标签和结构化数据
 */

const SITE_NAME = '语博仕朗思'
const SITE_URL = 'https://langsi.online/'
const SITE_LOGO = `${SITE_URL}brand-logo.png`
const SITE_IMAGE = `${SITE_URL}brand-cover.png`
const PAGE_SCHEMA_NAMES = ['article', 'faq', 'item-list', 'course']

function hasDocument() {
  return typeof document !== 'undefined'
}

function normalizeTitle(title) {
  if (!title) return ''
  if (title.includes(SITE_NAME)) return title
  return `${title} - ${SITE_NAME}`
}

function normalizeSchemaDate(value) {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toISOString()
}

function upsertMeta(selector, attributes, content) {
  if (!hasDocument() || !content) return
  let meta = document.querySelector(selector)
  if (!meta) {
    meta = document.createElement('meta')
    Object.keys(attributes).forEach((key) => {
      meta.setAttribute(key, attributes[key])
    })
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

function upsertJsonLd(name, schema, replace = true) {
  if (!hasDocument() || !schema) return
  const selector = `script[data-schema="${name}"]`
  const oldSchema = document.querySelector(selector)
  if (oldSchema && replace) {
    oldSchema.remove()
  } else if (oldSchema) {
    return
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-schema', name)
  script.textContent = JSON.stringify(schema)
  document.head.appendChild(script)
}

export function clearPageSchemas() {
  if (!hasDocument()) return
  PAGE_SCHEMA_NAMES.forEach((name) => {
    document.querySelectorAll(`script[data-schema="${name}"]`).forEach((schema) => schema.remove())
  })
}

/**
 * 更新页面 meta 标签
 * @param {Object} options - SEO 配置
 * @param {string} options.title - 页面标题
 * @param {string} options.description - 页面描述
 * @param {string} options.keywords - 关键词
 * @param {string} options.canonical - 页面规范 URL
 * @param {string} options.url - 页面 URL (兼容旧调用)
 * @param {string} options.robots - robots 指令
 */
export function updateMeta({ title, description, keywords, canonical, url, robots, ogTitle, ogDescription, ogUrl, ogType, image }) {
  if (!hasDocument()) return

  const canonicalUrl = canonical || url
  const finalTitle = normalizeTitle(title)

  // 更新 title
  if (finalTitle) {
    document.title = finalTitle
  }

  // 更新或创建 meta description
  upsertMeta('meta[name="description"]', { name: 'description' }, description)

  // 更新或创建 meta keywords
  upsertMeta('meta[name="keywords"]', { name: 'keywords' }, keywords)

  // 更新或创建 robots
  upsertMeta('meta[name="robots"]', { name: 'robots' }, robots || 'index, follow')

  // 更新 canonical URL
  if (canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = canonicalUrl
  }

  // Open Graph 基础字段
  upsertMeta('meta[property="og:title"]', { property: 'og:title' }, ogTitle || finalTitle)
  upsertMeta('meta[property="og:description"]', { property: 'og:description' }, ogDescription || description)
  upsertMeta('meta[property="og:url"]', { property: 'og:url' }, ogUrl || canonicalUrl)
  upsertMeta('meta[property="og:type"]', { property: 'og:type' }, ogType || 'website')
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME)
  upsertMeta('meta[property="og:image"]', { property: 'og:image' }, image || SITE_IMAGE)
  upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, `${SITE_NAME} LanguageCert 朗思备考平台`)
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image')
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, ogTitle || finalTitle)
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, ogDescription || description)
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image || SITE_IMAGE)
}

/**
 * 添加 FAQ 结构化数据 (JSON-LD)
 * @param {Array} faqs - FAQ 列表 [{question, answer}]
 */
export function addFAQSchema(faqs) {
  if (!faqs || faqs.length === 0) return

  const validFaqs = faqs.filter((faq) =>
    (faq.question || faq.title) && (faq.answer || faq.introduction)
  )
  if (validFaqs.length === 0) return

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question || faq.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer || faq.introduction || ''
      }
    }))
  }

  upsertJsonLd('faq', schema)
}

/**
 * 添加文章结构化数据
 * @param {Object} article - 文章信息
 */
export function addArticleSchema(article) {
  if (!article) return

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.introduction || article.description,
    author: {
      '@type': 'Organization',
      name: '语博仕朗思'
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO
      }
    },
    datePublished: normalizeSchemaDate(article.createTime || article.datePublished),
    dateModified: normalizeSchemaDate(article.updateTime || article.dateModified || article.createTime || article.datePublished),
    mainEntityOfPage: article.url || article.canonical
  }

  upsertJsonLd('article', schema)
}

/**
 * 添加网站结构化数据
 */
export function addWebsiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: '语博仕朗思提供 LanguageCert 朗思考试 B2/C1 备考、在线模考、题库练习、写作批改和口语训练等服务',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://langsi.online/langsiCommonProblem?label={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  }

  upsertJsonLd('website', schema, false)
}

/**
 * 添加组织结构化数据
 */
export function addOrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    legalName: '西安语博仕教育科技有限责任公司',
    url: SITE_URL,
    email: 'lchighpass@163.com',
    logo: {
      '@type': 'ImageObject',
      url: SITE_LOGO,
      width: 216,
      height: 216
    },
    image: SITE_IMAGE,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '陕西省西安市长安区学府大街鹏润悦秀城10号楼1单元7层10718-AL0101室',
      addressLocality: '西安市',
      addressRegion: '陕西省',
      addressCountry: 'CN'
    }
  }

  upsertJsonLd('organization', schema, false)
}

/**
 * 添加列表结构化数据
 * @param {Object} options - 列表配置
 * @param {string} options.name - 列表名称
 * @param {Array} options.items - 列表项 [{ name, url, description }]
 */
export function addItemListSchema({ name, items }) {
  if (!items || items.length === 0) return

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url,
      description: item.description
    }))
  }

  upsertJsonLd('item-list', schema)
}

/**
 * 添加课程结构化数据
 * @param {Object} course - 课程信息
 */
export function addCourseSchema(course) {
  if (!course) return

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.name,
    description: course.description,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      sameAs: SITE_URL
    },
    url: course.url
  }

  upsertJsonLd('course', schema)
}

export default {
  clearPageSchemas,
  updateMeta,
  addFAQSchema,
  addArticleSchema,
  addWebsiteSchema,
  addOrganizationSchema,
  addItemListSchema,
  addCourseSchema
}
